/**
 * `outlineModel` — pure helpers that translate the outline editor's keyboard
 * gestures (Enter / Tab / Shift-Tab) into concrete tree mutations, plus the
 * depth-first row list the outline renders. Pure + dependency-free, so the
 * keyboard semantics are unit-testable without a DOM.
 *
 * Issue #759 (Phase 2, PR-2). §A7's outline editor is the "type a nested
 * list" gesture: Enter = new sibling, Tab = indent (re-parent under the
 * preceding sibling), Shift-Tab = outdent (re-parent to grandparent). Each
 * gesture resolves to *exactly one* server mutation; this module computes the
 * `{parentId, orderIndex}` that mutation needs, so the view stays a thin
 * shell over `useTaskNodes`'s create/patch hooks.
 *
 * The one place the outline's "what does this key do, given this row?" logic
 * lives — both the full-screen editor (B2.3) and the split-view left pane
 * (B2.4) consume it, so the keyboard model can never drift between the two.
 */

import type { TreeNode } from "../tree/buildTree";
import { flattenTree } from "../tree/buildTree";

/** One rendered outline row: the node plus its rendering depth. */
export interface OutlineRow {
  node: TreeNode;
  depth: number;
}

/**
 * The depth-first ordered rows the outline renders — the same order
 * `flattenTree` produces (the rail uses this too), so the outline and the
 * canvas agree on ordering. Each row carries its node's `depth` for indent.
 */
export function outlineRows(roots: readonly TreeNode[]): OutlineRow[] {
  return flattenTree(roots).map((node) => ({ node, depth: node.depth }));
}

/**
 * The rows to render given a set of collapsed parent ids — the descendants of
 * a collapsed node are hidden (the row for the collapsed node itself stays, so
 * it can still be expanded). Pure: it walks the forest depth-first and prunes
 * the subtree under any collapsed node.
 *
 * Reuses the SAME `collapsed` set the canvas reads (`treeStore.collapsed`,
 * B8.3), so collapsing in the outline hides the same subtrees the canvas folds
 * — one source of truth across both surfaces (#1043). When `collapsed` is empty
 * this is identical to `outlineRows` (the byte-unchanged legacy behaviour).
 */
export function visibleOutlineRows(
  roots: readonly TreeNode[],
  collapsed: ReadonlySet<string>,
): OutlineRow[] {
  const out: OutlineRow[] = [];
  const walk = (node: TreeNode) => {
    out.push({ node, depth: node.depth });
    // A collapsed node keeps its own row but hides its descendants.
    if (collapsed.has(node.id)) return;
    for (const child of node.children) walk(child);
  };
  for (const root of roots) walk(root);
  return out;
}

/** Find a node anywhere in the forest by id (depth-first). */
export function findNode(roots: readonly TreeNode[], id: string): TreeNode | null {
  return flattenTree(roots).find((n) => n.id === id) ?? null;
}

/** The ordered siblings of `node` (the children of its parent, or the roots). */
export function siblingsOf(roots: readonly TreeNode[], node: TreeNode): TreeNode[] {
  if (node.parentId === null) return [...roots];
  const parent = findNode(roots, node.parentId);
  return parent ? [...parent.children] : [];
}

/** The 0-based index of `node` among its siblings (−1 if not found). */
export function indexAmongSiblings(roots: readonly TreeNode[], node: TreeNode): number {
  return siblingsOf(roots, node).findIndex((s) => s.id === node.id);
}

/** Where a brand-new node should be created. */
export interface CreateTarget {
  parentId: string | null;
  orderIndex: number;
}

/** Where an existing node should move to (re-parent + re-order). */
export interface MoveTarget {
  parentId: string | null;
  orderIndex: number;
}

/**
 * Enter on `node`: a new sibling *immediately after* it (same parent, the
 * order slot right after this row). Returns the create target for POST /nodes.
 */
export function siblingCreateTarget(roots: readonly TreeNode[], node: TreeNode): CreateTarget {
  const idx = indexAmongSiblings(roots, node);
  return { parentId: node.parentId, orderIndex: idx + 1 };
}

/** A new root outcome appended after the last existing root (the "+ outcome"). */
export function rootCreateTarget(roots: readonly TreeNode[]): CreateTarget {
  return { parentId: null, orderIndex: roots.length };
}

/**
 * Tab on `node` (indent): re-parent it under its **preceding sibling**, as
 * that sibling's last child. Returns null when there's no preceding sibling
 * (the first row at a level can't indent — a no-op, like every outline tool).
 */
export function indentTarget(roots: readonly TreeNode[], node: TreeNode): MoveTarget | null {
  const siblings = siblingsOf(roots, node);
  const idx = siblings.findIndex((s) => s.id === node.id);
  if (idx <= 0) return null;
  const newParent = siblings[idx - 1] as TreeNode;
  return { parentId: newParent.id, orderIndex: newParent.children.length };
}

/**
 * Shift-Tab on `node` (outdent): re-parent it to its **grandparent**, slotted
 * immediately after its current parent. Returns null when the node is already
 * a root (nothing to outdent to).
 */
export function outdentTarget(roots: readonly TreeNode[], node: TreeNode): MoveTarget | null {
  if (node.parentId === null) return null;
  const parent = findNode(roots, node.parentId);
  if (!parent) return null;
  const grandparentId = parent.parentId; // null ⇒ becomes a root
  const parentSiblings = siblingsOf(roots, parent);
  const parentIdx = parentSiblings.findIndex((s) => s.id === parent.id);
  return { parentId: grandparentId, orderIndex: parentIdx + 1 };
}

/**
 * Drag-reorder among siblings: move `node` so it sits at visual position
 * `toIndex` within its current sibling list. Returns the new order_index, or
 * null when the move is a no-op (same slot). Only reorders within the same
 * parent (re-parenting via drag is the tree canvas's job, §A7).
 */
export function reorderTarget(
  roots: readonly TreeNode[],
  node: TreeNode,
  toIndex: number,
): { orderIndex: number } | null {
  const idx = indexAmongSiblings(roots, node);
  if (idx < 0 || idx === toIndex) return null;
  return { orderIndex: toIndex };
}

/** A single `order_index` swap between two sibling nodes (#961). */
export interface SiblingSwap {
  /** The node the user is moving. */
  nodeId: string;
  /** Its target `order_index` (the neighbour's current one). */
  nodeOrderIndex: number;
  /** The adjacent sibling that gets displaced. */
  neighbourId: string;
  /** The neighbour's target `order_index` (the moving node's current one). */
  neighbourOrderIndex: number;
}

/**
 * Move `node` one step among its siblings (#961, "arrange for viewing"). This
 * respects §A7: the parent never changes and no x/y is set — only the left→right
 * ORDER, which the tidy auto-layout then re-tiles from. Roots reorder among
 * roots; children reorder among that parent's children.
 *
 * We compute a **swap of `order_index` values** between the moving node and its
 * adjacent visual neighbour (the prior sibling for `"earlier"`, the next for
 * `"later"`). Swapping two *distinct* values is unambiguous and self-stabilising
 * — it never relies on `buildTree`'s title/id tie-break (which a naive
 * "set order_index = targetIndex" would, since the displaced sibling keeps its
 * value and the two then tie). `order_index` is an int column server-side, so a
 * fractional midpoint isn't an option; the swap keeps everything integral.
 *
 * Returns `null` at the ends (first sibling can't move earlier; last can't move
 * later) — the caller disables the control there.
 */
export function siblingSwap(
  roots: readonly TreeNode[],
  node: TreeNode,
  direction: "earlier" | "later",
): SiblingSwap | null {
  const siblings = siblingsOf(roots, node);
  const idx = siblings.findIndex((s) => s.id === node.id);
  if (idx < 0) return null;
  const neighbourIdx = direction === "earlier" ? idx - 1 : idx + 1;
  if (neighbourIdx < 0 || neighbourIdx >= siblings.length) return null;
  const neighbour = siblings[neighbourIdx] as TreeNode;
  return {
    nodeId: node.id,
    nodeOrderIndex: neighbour.orderIndex,
    neighbourId: neighbour.id,
    neighbourOrderIndex: node.orderIndex,
  };
}

/**
 * Whether `node` can move `"earlier"` / `"later"` among its siblings (#961) —
 * thin wrapper over `siblingSwap`. The card-level ◀/▶ caller was removed in
 * round-4 item ② (#1043); this stays as a pure scaffold for any future
 * outline-side reorder gesture.
 */
export function canReorderSibling(
  roots: readonly TreeNode[],
  node: TreeNode,
  direction: "earlier" | "later",
): boolean {
  return siblingSwap(roots, node, direction) !== null;
}

/**
 * Would re-parenting `node` under `newParentId` create a cycle (i.e. is the
 * proposed parent the node itself or one of its descendants)? The server is
 * the source of truth (it returns 422 `TaskCycleError`), but checking here
 * lets the canvas reject an illegal drop *before* a round-trip — snappier and
 * avoids a doomed mutation.
 */
export function wouldCreateCycle(node: TreeNode, newParentId: string): boolean {
  if (node.id === newParentId) return true;
  const stack = [...node.children];
  while (stack.length > 0) {
    const child = stack.pop() as TreeNode;
    if (child.id === newParentId) return true;
    stack.push(...child.children);
  }
  return false;
}
